import { FC, ReactNode, createContext, useContext, useRef } from 'react';

/** Сообщение валидации одного поля формы */
type FieldMessageType = {
	/** Имя поля */
	name: string;
	/** Текст сообщения, пустая строка - показывать нечего */
	message: string;
};

/** Реестр сообщений одной формы */
type FieldMessagesRegistryType = {
	/** Сообщения полей в порядке отрисовки на экране */
	messages: FieldMessageType[];
	/** Тексты, дубли которых схлопываем */
	collapsed: string[];
};

const FieldMessagesContext = createContext<FieldMessagesRegistryType | null>(
	null
);

interface IFieldMessagesProviderProps {
	/**
	 * Тексты валидации, дубли которых нужно схлопнуть: такой текст показывается
	 * только у первого поля сверху(про обязательность поля)
	 */
	collapsed: string[];
	children?: ReactNode;
}

/**
 * Оборачивает поля формы и схлопывает одинаковые сообщения валидации: текст 
 * остаётся только у первого поля сверху, у остальных полей остаётся
 * красная подсветка без текста. Сообщения с уникальным текстом (неверный
 * формат и т.п.) показываются всегда у своего поля. Поля вне провайдера ведут
 * себя как обычно и показывают все сообщения
 */
export const FieldMessagesProvider: FC<IFieldMessagesProviderProps> = ({
	collapsed,
	children,
}) => {
	const registryRef = useRef<FieldMessagesRegistryType>({
		messages: [],
		collapsed,
	});

	// Список пересобирается на каждый рендер формы. Провайдер рендерится раньше
	// своих детей, поэтому дальше поля дописывают себя в том порядке, в каком
	// стоят на экране - включая те, что появляются по условию
	registryRef.current.messages = [];
	registryRef.current.collapsed = collapsed;

	return (
		<FieldMessagesContext.Provider value={registryRef.current}>
			{children}
		</FieldMessagesContext.Provider>
	);
};

/**
 * показывать ли текст сообщения под полем. Поле по ходу
 * рендера сообщает свой текст в реестр формы, а реестр говорит, первое ли оно
 * с таким текстом. Полю достаточно знать про поля выше себя
 */
export const useFieldMessageVisible = (
	name: string,
	message: string,
	hasMessage: boolean
) => {
	const registry = useContext(FieldMessagesContext);

	if (!registry) {
		return hasMessage;
	}

	const currentMessage = hasMessage ? message : '';
	const index = registry.messages.findIndex((item) => {
		return item.name === name;
	});

	if (index === -1) {
		registry.messages.push({ name, message: currentMessage });
	} else {
		registry.messages[index] = { name, message: currentMessage };
	}

	if (!hasMessage || !registry.collapsed.includes(message)) {
		return hasMessage;
	}

	const firstWithMessage = registry.messages.find((item) => {
		return item.message === message;
	});

	return firstWithMessage?.name === name;
};
